import type { IMedium } from "@op/puck-types";
// @ts-ignore
import placeholderImage from "../../../../../assets/placeholder.svg?url&no-inline";
import {
  cls,
  clx,
  isUrl,
  makeEditorImageUrl,
  makeFigureId,
  makeRendererImageUrl,
  MediaPreview,
  useContent,
  useRender,
  useTranslation,
} from "@op/utils";
import { useMemo } from "react";
import { Dc_Media_Figures } from "@op/sf-op";
import {
  Root,
  Trigger,
  Portal,
  Overlay,
  Content,
} from "@radix-ui/react-dialog";

export interface MediumFigureProps {
  medium: IMedium;
  cover?: boolean;
}

function getViewType(
  detail: boolean,
  source?: string,
): "reading-comp" | "source-expand" | "expand" | "source" | "none" {
  if (source && isUrl(source)) {
    if (!detail) return "source";
    return "source-expand";
  } else {
    if (!detail) return "none";
    return "expand";
  }
}

export const MediumFigure = ({ medium, cover }: MediumFigureProps) => {
  const { context, apiUrl } = useRender();
  const { meta } = useContent();

  const isNotRenderer = useMemo(() => context !== "renderer", [context]);

  const imageUrl = useMemo(() => {
    if (!medium || !medium.uploadId) return "";
    switch (context) {
      case "editor":
        return makeEditorImageUrl(medium.uploadId, apiUrl);
      case "renderer":
        return makeRendererImageUrl(medium.uploadId, apiUrl);
      default:
        return "";
    }
  }, [context, apiUrl, medium]);

  const image = useMemo(() => {
    return (
      <>
        {imageUrl ? (
          <MediaPreview
            cover={cover}
            src={imageUrl}
            alt={medium.metadata?.caption || ""}
            mimeType={medium.mimeType}
            width={medium.width}
            height={medium.height}
            loading="lazy"
          />
        ) : (
          <MediaPreview
            cover={cover}
            mimeType="image/svg+xml"
            src={placeholderImage}
            width={600}
            height={600}
            loading="lazy"
          />
        )}
      </>
    );
  }, [imageUrl, medium, cover]);

  const viewType = useMemo(
    () =>
      getViewType(
        medium.metadata?.isExpandable ?? false,
        medium.metadata?.source?.url,
      ),
    [medium],
  );

  const { figure: trans } = useTranslation();

  return (
    <Root>
      <Dc_Media_Figures
        id={makeFigureId(medium.id)}
        isPrint={false}
        noContainer={meta?.readingSettings?.noContainer ?? false}
        image={
          <Trigger
            asChild
            disabled={isNotRenderer || medium.metadata?.isExpandable !== true}
          >
            <div
              className={cls(
                "cursor-pointer",
                clx({
                  "border border-solid hover:border-brand-primary":
                    medium.metadata?.isExpandable ?? false,
                }),
              )}
            >
              {image}
            </div>
          </Trigger>
        }
        buttonStackSlot={
          <Dc_Media_Figures.ButtonStack
            variant={viewType}
            sourceButton={
              !!medium.metadata?.source?.url && (
                <a
                  href={medium.metadata.source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Dc_Media_Figures.SourceButton>
                    {trans.viewSource}
                  </Dc_Media_Figures.SourceButton>
                </a>
              )
            }
            expandButton={
              medium.metadata?.isExpandable && (
                <Trigger
                  asChild
                  disabled={
                    isNotRenderer || medium.metadata?.isExpandable !== true
                  }
                >
                  <Dc_Media_Figures.ExpandButton>
                    {trans.expand}
                  </Dc_Media_Figures.ExpandButton>
                </Trigger>
              )
            }
          />
        }
      >
        {medium.metadata?.caption && (
          <Dc_Media_Figures.Description>
            {medium.metadata.caption}
          </Dc_Media_Figures.Description>
        )}
        {medium.metadata?.source.title && (
          <Dc_Media_Figures.SourceDescription>
            {medium.metadata.source.title}
          </Dc_Media_Figures.SourceDescription>
        )}
      </Dc_Media_Figures>
      <Portal>
        <Overlay className="fixed z-20 inset-0 bg-black/50 data-[state=open]:animate-overlayShow" />
        <Content className="fixed z-30 left-1/2 top-1/2 max-w-[90vw] w-full -translate-x-1/2 -translate-y-1/2 data-[state=open]:animate-contentShow">
          {image}
        </Content>
      </Portal>
    </Root>
  );
};
